import { getUploadHandler } from '@/services/upload/registry'
import {
  getUploadTabId,
  isUploadOwnedByAnotherTab,
  withUploadOwnership,
} from '@/services/upload/tab-coordinator'
import i18n from '@/i18n'

const STORAGE_KEY = 'mcFileUploads'
const ACTIVE_STATUSES = ['uploading', 'processing', 'canceling']
const AUTO_OPEN_STATUSES = [...ACTIVE_STATUSES, 'paused', 'waiting_resume']
const UPLOAD_INTERRUPTED_KEY = 'global.fileUploader.messages.uploadInterrupted'

export const state = {
  uploads: [],
  isPopupUpload: false,
}

export const getters = {
  activeUploads: (state) => state.uploads.filter((upload) => ACTIVE_STATUSES.includes(upload.status)),
  completedUploads: (state) => state.uploads.filter((upload) => upload.status === 'completed'),
  failedUploads: (state) => state.uploads.filter((upload) => upload.status === 'failed'),
  canceledUploads: (state) => state.uploads.filter((upload) => upload.status === 'canceled'),
}

const serializeUpload = (upload) => {
  const serializedUpload = { ...upload }
  delete serializedUpload.file
  return serializedUpload
}

const saveUploads = (uploads) => {
  if (!uploads.length) {
    localStorage.removeItem(STORAGE_KEY)
    return
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(uploads.map(serializeUpload)))
}

const canResumeUpload = (upload) => {
  const handler = getUploadHandler(upload.uploadType, upload)
  return handler?.canResume ? handler.canResume(upload) : false
}

export const mutations = {
  addUpload(state, payload) {
    state.uploads.unshift(payload)
  },
  replaceUploads(state, uploads) {
    state.uploads.splice(0, state.uploads.length, ...uploads)
  },
  updateUpload(state, { id, ...updates }) {
    const index = state.uploads.findIndex((upload) => upload.id === id)
    if (index !== -1) {
      state.uploads.splice(index, 1, { ...state.uploads[index], ...updates })
    }
  },
  cancelUpload(state, id) {
    const index = state.uploads.findIndex((upload) => upload.id === id)
    if (index !== -1) {
      state.uploads.splice(index, 1, {
        ...state.uploads[index],
        status: 'canceled',
        statusAt: new Date().toISOString(),
      })
    }
  },
  removeUpload(state, id) {
    const index = state.uploads.findIndex((upload) => upload.id === id)
    if (index !== -1) state.uploads.splice(index, 1)
  },
  completeUpload(state, id) {
    const index = state.uploads.findIndex((upload) => upload.id === id)
    if (index !== -1) {
      state.uploads.splice(index, 1, {
        ...state.uploads[index],
        status: 'completed',
        progress: 100,
        statusAt: new Date().toISOString(),
      })
    }
  },
  failUpload(state, { id, error, errorKey = null }) {
    const index = state.uploads.findIndex((upload) => upload.id === id)
    if (index !== -1) {
      state.uploads.splice(index, 1, {
        ...state.uploads[index],
        status: 'failed',
        error,
        errorKey,
        statusAt: new Date().toISOString(),
      })
    }
  },
  showUploadPopup(state, payload) {
    state.isPopupUpload = payload
  },
}

const generateUploadId = () =>
  Date.now().toString(36) + Math.random().toString(36).substr(2)

export const actions = {
  hydrateUploads({ commit }) {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (!saved) return

    try {
      const uploads = JSON.parse(saved)
      if (!Array.isArray(uploads) || !uploads.length) return

      const hydratedUploads = uploads.map((upload) => {
        if (upload.status === 'canceling') {
          return {
            ...upload,
            status: 'canceled',
            statusAt: new Date().toISOString(),
          }
        }

        const isResumable = canResumeUpload(upload)
        if (ACTIVE_STATUSES.includes(upload.status) && isUploadOwnedByAnotherTab(upload)) {
          return upload
        }

        if (ACTIVE_STATUSES.includes(upload.status) && isResumable) {
          return {
            ...upload,
            status: 'waiting_resume',
            error: null,
            errorKey: null,
            interruptionReason: 'page-refresh',
            statusAt: new Date().toISOString(),
          }
        }

        if (['paused', 'waiting_resume'].includes(upload.status) && isResumable) {
          const hasRuntimeError = upload.interruptionReason === 'runtime-error'
          const hasLegacyInterruptedError = ['Upload terganggu', 'Upload interrupted'].includes(
            upload.error
          )
          return {
            ...upload,
            error: hasRuntimeError && !hasLegacyInterruptedError ? upload.error : null,
            errorKey:
              hasRuntimeError && (upload.errorKey || hasLegacyInterruptedError)
                ? UPLOAD_INTERRUPTED_KEY
                : null,
            interruptionReason: hasRuntimeError
              ? upload.interruptionReason
              : upload.interruptionReason || 'page-refresh',
          }
        }

        if (!ACTIVE_STATUSES.includes(upload.status)) return upload

        return {
          ...upload,
          status: 'failed',
          error: null,
          errorKey: 'global.fileUploader.messages.interruptedNoResume',
          statusAt: new Date().toISOString(),
        }
      })

      commit('replaceUploads', hydratedUploads)
      commit(
        'showUploadPopup',
        hydratedUploads.some((upload) => AUTO_OPEN_STATUSES.includes(upload.status))
      )
      saveUploads(hydratedUploads)
    } catch {
      commit('replaceUploads', [])
    }
  },

  persistUploads({ state }) {
    saveUploads(state.uploads)
  },

  async cancelAllUploads({ state, commit }, { apollo }) {
    const uploads = state.uploads.filter((upload) =>
      ['uploading', 'processing', 'paused', 'waiting_resume', 'canceling'].includes(upload.status)
    )

    await Promise.allSettled(
      uploads.map(async (upload) => {
        const handler = getUploadHandler(upload.uploadType, upload)
        commit('updateUpload', { id: upload.id, status: 'canceling' })
        if (handler?.cancel) await handler.cancel({ upload, apollo })
        commit('cancelUpload', upload.id)
      })
    )

    uploads.forEach((upload) => commit('cancelUpload', upload.id))
    commit('showUploadPopup', false)
    saveUploads(state.uploads)
  },

  async startUpload({ state, commit }, { file, options = {} }) {
    const { customUploadHandler, ...uploadOptions } = options
    const uploadId = generateUploadId()

    commit('addUpload', {
      id: uploadId,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      fileLastModified: file.lastModified,
      status: 'uploading',
      progress: 0,
      ownerTabId: getUploadTabId(),
      ownerHeartbeatAt: Date.now(),
      file,
      ...uploadOptions,
    })
    commit('showUploadPopup', true)
    saveUploads(state.uploads)

    if (!customUploadHandler) {
      const error = i18n.t('global.fileUploader.messages.handlerRequired')
      commit('failUpload', { id: uploadId, error })
      saveUploads(state.uploads)
      throw new Error(error)
    }

    const persistUpdate = (updates) => {
      commit('updateUpload', { id: uploadId, ...updates })
      saveUploads(state.uploads)
    }

    try {
      const ownership = await withUploadOwnership({
        uploadId,
        onOwnershipChange: persistUpdate,
        task: () =>
          customUploadHandler({
            file,
            uploadId,
            updateProgress: (progress) => persistUpdate({ progress }),
            updateStatus: (status) => persistUpdate({ status }),
            updateMetadata: persistUpdate,
            complete: () => {
              commit('completeUpload', uploadId)
              saveUploads(state.uploads)
            },
            cancel: () => {
              commit('cancelUpload', uploadId)
              saveUploads(state.uploads)
            },
            fail: (error, errorKey = null) => {
              commit('failUpload', { id: uploadId, error, errorKey })
              saveUploads(state.uploads)
            },
          }),
      })

      if (!ownership.acquired) return uploadId
    } catch (error) {
      if (error.name !== 'AbortError') {
        commit('failUpload', { id: uploadId, error: null, errorKey: UPLOAD_INTERRUPTED_KEY })
        saveUploads(state.uploads)
      }
      throw error
    }

    return uploadId
  },
}
